<script setup lang="ts">
  import { inject, onMounted, ref } from 'vue';
  import { AlcAlert, AlcButton, AlcInput, AlertTypeEnum, ButtonVariantEnum } from '@alchemer/vue-component-library';
  import { useAuth0 } from '@auth0/auth0-vue';
  import { useDebounceFn, useUrlSearchParams } from '@vueuse/core';

  import { fetchAllAuthMethods } from '@/api/';
  import { LoginTypeEnum } from '@/types/enums';
  import { ChangeLoginTypeKey } from '@/types/symbols';
  import { EnvironmentalConfigKey } from '@/utils/environmental-config';
  import { LegacyDataCenterStorage } from '@/utils/local-storage';

  const { loginWithRedirect } = useAuth0();
  const changeLogin = inject(ChangeLoginTypeKey, () => {});
  const environmentalConfig = inject(EnvironmentalConfigKey);
  const isVerifying = ref<boolean>(false);
  const params = useUrlSearchParams('history');
  const userEmail = ref<string | undefined>();
  const warningMessage = ref<string | undefined>('');

  let alertType = AlertTypeEnum.WARNING;

  const handleContinueClick = useDebounceFn(async () => {
    isVerifying.value = true;
    warningMessage.value = '';

    // https://www.prosiglieres.com/posts/rfc-compliant-email-address-validation/
    if (!userEmail.value) {
      warningMessage.value = 'Please enter an email address.';
      isVerifying.value = false;
      return;
    }
    try {
      if (! environmentalConfig) {
       throw new Error('environmentalConfig is not defined');
      }

      /*
        If a user who ONLY has a legacy survey account (aka, no Auth0),
        was redirected from a Survey Datacenter to the LoginSPA,
        we should redirect them to that legacy datacenter for login,
        but ONLY if they have a legacy account in that datacenter.

        The 'rld' query param is the datacenter to redirect to and it is
        set in Controller.php of the php monolith
      */
      const overridePriority = params.rld?.toString() || '';
      const datacenterKeys = environmentalConfig.getDatacenterPriority(overridePriority);

      // gets responses from all datacenters
      const results = await fetchAllAuthMethods(userEmail.value, datacenterKeys, environmentalConfig);

      let legacyDatacenters:string[] = [];

      for (let i = 0; i < datacenterKeys.length; i++) {
        const response = results[i];

        if (
          response.status !== 'fulfilled'
          || ! response.value.ok
        ) {
          continue;
        }

        const data = await response.value.json();
        const datacenter = datacenterKeys[i];

        if (! data.success) {
            // the 'l' property marks whether an Auth0 user has a legacy account in this datacenter
            if (data?.l) {
              legacyDatacenters.push(datacenter);
            }
            continue;
        }

        window.location.href = environmentalConfig.getSurveyLegacyLoginUrl(datacenter, userEmail.value);
        return;
      }

      const legacyDataCenterStorage = new LegacyDataCenterStorage();
      legacyDataCenterStorage.clearAll();

      if (legacyDatacenters.length > 0) {
        legacyDataCenterStorage.setItems(legacyDatacenters);
      }

      // if no legacy routes are used continue to auth0
      loginWithRedirect({
        authorizationParams: {
          login_hint: userEmail.value,
        },
      });

    } catch (error) {
      // on error go to auth0
      loginWithRedirect({
        authorizationParams: {
          login_hint: userEmail.value,
        },
      });
    } finally {
      isVerifying.value = false;
      handleEmailChange('');
    }
  }, 250);

  const handleEmailChange = (value: string) => {
    userEmail.value = value?.trim() ?? '';
    warningMessage.value = '';
  };

  const handleSocialConnectionClick = (socialConnection: string) => {
    loginWithRedirect({
      authorizationParams: {
        connection: socialConnection,
      },
    });
  };

  const handleSSOClick = () => {
    changeLogin(LoginTypeEnum.SSO);
  };

  onMounted(() => {
    // passed in messaging to be displayed
    if (params.msg) {
      warningMessage.value = `${params.msg}`;
    }

    // Type of alert to display
    if (params.at) {
      alertType = params.at as AlertTypeEnum;
    }
  });
</script>
<template>
  <div class="login-dialog">
    <div class="login-dialog__form">
      <div>
        <AlcInput
          id="email"
          :default-value="userEmail"
          placeholder="Enter your email address"
          @input-changed="handleEmailChange"
          @input-reset="handleEmailChange('')"
          @keyup.enter="handleContinueClick"
        />
      </div>
      <div
        v-show="warningMessage && warningMessage.trim().length > 0"
        class="login-dialog__form-message"
      >
        <AlcAlert :alertType="alertType">{{ warningMessage }}</AlcAlert>
      </div>
      <div>
        <AlcButton
          :stretch="true"
          label="Continue"
          :disabled="isVerifying"
          @alc-btn-click="handleContinueClick"
        />
      </div>
      <div><a href="https://www.alchemer.com/plans-pricing/small-teams/">Need an account?</a></div>
      <div>or</div>
      <div class="login-dialog__form-additional-options">
        <div class="login-dialog__form-additional-options-label">Additional Login Options</div>
        <div>
          <AlcButton
            :stretch="true"
            :variant="ButtonVariantEnum.SECONDARY"
            label="Sign in with Github"
            @alc-btn-click="handleSocialConnectionClick('github')"
          />
        </div>
        <div>
          <AlcButton
            :stretch="true"
            :variant="ButtonVariantEnum.SECONDARY"
            label="Sign in with Google"
            @alc-btn-click="handleSocialConnectionClick('google-oauth2')"
          />
        </div>
        <div>
          <AlcButton
            :stretch="true"
            :variant="ButtonVariantEnum.SECONDARY"
            label="Sign in with Alchemer Account Alias"
            @alc-btn-click="handleSSOClick"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  .login-dialog {
    width: 100%;
    display: flex;
    flex-direction: column;
    color: var(--alc-base-800);

    &__form {
      display: flex;
      flex-direction: column;
      gap: 16px;
      align-items: center;

      & > div {
        text-align: center;
        width: 100%;
        font-size: 14px;
      }
    }

    &__form-additional-options {
      display: flex;
      flex-direction: column;
      margin-top: 16px;
      gap: 8px;
    }
    &__form-additional-options-label {
      text-align: left;
      font-size: 16px;
      font-style: normal;
      font-weight: 400;
      line-height: normal;
    }
  }
</style>
